// src/components/DocumentDetails.js
import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useReactToPrint } from "react-to-print";
import { Button, Modal } from "react-bootstrap";
import InvoiceEdit from "./InvoiceEdit";

const DocumentDetails = () => {
  const { user } = useContext(UserContext);
  const contentToPrint = useRef(null);

  const [invoice, setInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalInfo, setModalInfo] = useState({ title: "", body: "" });
  const [refresh, setRefresh] = useState(false);

  const { id } = useParams();

  const handlePrint = useReactToPrint({
    content: () => contentToPrint.current,
  });

  const AddressRenderer = (address) => {
    if (!address) return null;
    return address.split(",").map((part, i) => (
      <p key={i} style={{ margin: 0 }}>
        {part.trim()}
      </p>
    ));
  };

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/invoices/invoice/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setInvoice(response.data.invoice);
        setInvoiceItems(response.data.invoiceItems);
      } catch (error) {
        console.error("Error fetching invoice:", error);
      }
    };

    fetchInvoiceDetails();
  }, [id, modalShow]);

  useEffect(() => {
    if (!invoice) return;

    const fetchCustomer = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/customers/customer/${invoice.CustomerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setCustomer(response.data);
      } catch (error) {
        console.error("Error fetching customer:", error);
      }
    };

    fetchCustomer();
  }, [invoice, refresh]);

  if (!invoice || !customer) return null;

  function MyVerticallyCenteredModal(props) {
    return (
      <Modal {...props} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{props.modalInfo.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{props.modalInfo.body}</Modal.Body>
        <Modal.Footer>
          <Button onClick={props.onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <div style={styles.page}>
      {/* ACTIONS */}
      <div style={styles.actions}>
        <button className="btn btn-outline-dark" onClick={handlePrint}>
          Print
        </button>

        <button
          className="btn btn-dark"
          onClick={() => {
            setModalShow(true);
            setModalInfo({
              title: invoice.invoiceNumber,
              body: (
                <InvoiceEdit
                  invoice={invoice}
                  invoiceItems={invoiceItems}
                  setModalShow={setModalShow}
                  customer={customer}
                  setRefresh={setRefresh}
                />
              ),
            });
          }}
        >
          Edit
        </button>
      </div>

      {/* INVOICE CARD */}
      <div style={styles.invoiceCard} ref={contentToPrint}>
        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.businessName}>{user.businessName}</h2>
            {AddressRenderer(user.businessAddress)}
          </div>

          <div style={styles.summaryBox}>
            <h3 style={styles.invoiceTitle}>INVOICE</h3>
            <p>
              <strong>No:</strong> {invoice.invoiceNumber}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {new Date(invoice.date).toLocaleDateString()}
            </p>
            <p>
              <strong>Due:</strong> {invoice.dueDate}
            </p>
          </div>
        </div>

        {/* BILL TO */}
        <div style={styles.billTo}>
          <h5 style={styles.sectionTitle}>Bill To</h5>
          <p style={{ fontWeight: 600 }}>{customer.name}</p>
          {AddressRenderer(customer.address)}
        </div>

        {/* DESCRIPTION */}
        {invoice.desc && (
          <div style={styles.description}>
            <strong>{invoice.desc}</strong>
          </div>
        )}

        {/* TABLE */}
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Units</th>
              <th style={thStyle}>Unit Price</th>
              <th style={thStyle}>Total</th>
            </tr>
          </thead>
          <tbody>
            {invoiceItems.map((item, index) => (
              <tr key={index} style={styles.tableRow}>
                <td style={tdStyle}>
                  {item.description.replace(/\\n/g, "\n")}
                </td>
                <td style={tdStyle}>{item.units}</td>
                <td style={tdStyle}>${item.unitPrice}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>
                  ${item.totalPrice}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* TOTAL */}
        <div style={styles.totalSection}>
          <div style={styles.totalBox}>TOTAL: ${invoice.total}</div>
        </div>

        {/* PAYMENT DETAILS */}
        <div style={styles.paymentCard}>
          <h5 style={styles.sectionTitle}>Payment Details</h5>
          <p>Bank: {user.bankName}</p>
          <p>Account Name: {user.bankAccName}</p>
          <p>Account Number: {user.bankAccNumber}</p>
          <p>IBAN/SWIFT: {user.bankAccIBAN}</p>
          <p>Currency: {user.currency}</p>
        </div>
      </div>

      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        modalInfo={modalInfo}
      />
    </div>
  );
};

/* ===============================
   STYLES
=================================*/

const styles = {
  page: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "40px 20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: "#f4f6f9",
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "15px",
    marginBottom: 30,
  },

  invoiceCard: {
    background: "#ffffff",
    padding: 50,
    borderRadius: 6,
    border: "1px solid #e5e7eb",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: "2px solid #111827",
    paddingBottom: 25,
    marginBottom: 30,
  },

  businessName: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: 1,
  },

  invoiceTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: 2,
  },

  summaryBox: {
    textAlign: "right",
    fontSize: 14,
    lineHeight: 1.6,
  },

  billTo: {
    marginBottom: 30,
  },

  description: {
    marginBottom: 20,
  },

  sectionTitle: {
    marginBottom: 8,
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#6b7280",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  thead: {
    background: "#f9fafb",
  },

  tableRow: {
    borderBottom: "1px solid #e5e7eb",
  },

  totalSection: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 40,
  },

  totalBox: {
    background: "#111827",
    color: "#fff",
    padding: "15px 40px",
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: 1,
  },

  paymentCard: {
    marginTop: 50,
    paddingTop: 25,
    borderTop: "1px solid #e5e7eb",
    fontSize: 14,
    lineHeight: 1.6,
  },
};

const thStyle = {
  padding: "12px 10px",
  textAlign: "left",
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "#6b7280",
};

const tdStyle = {
  padding: "14px 10px",
  fontSize: 14,
  verticalAlign: "top",
  whiteSpace: "pre-line",
};

export default DocumentDetails;
